/**
 * Ajustes → Correo saliente (SMTP de la compañía). Solo ADMIN.
 * Se usa para enviar presupuestos (y futuros avisos) por email.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, Loader2, Save, Send } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import type { FirmSmtpStatus } from '@toga/shared'

const inputCls =
  'w-full px-2.5 py-1.5 rounded-lg bg-[var(--bg-input-soft)] border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

export default function EmailSettingsPage() {
  const { t } = useTranslation('settings')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const [host, setHost] = useState('')
  const [port, setPort] = useState('465')
  const [secure, setSecure] = useState(true)
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [hasPassword, setHasPassword] = useState(false)
  const [fromEmail, setFromEmail] = useState('')
  const [fromName, setFromName] = useState('')
  const [testTo, setTestTo] = useState('')

  useEffect(() => {
    api
      .get<FirmSmtpStatus>('/firm/smtp')
      .then((s) => {
        setHost(s.smtpHost ?? '')
        setPort(String(s.smtpPort))
        setSecure(s.smtpSecure)
        setUser(s.smtpUser ?? '')
        setHasPassword(s.hasPassword)
        setFromEmail(s.smtpFromEmail ?? '')
        setFromName(s.smtpFromName ?? '')
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('email.errors.load')))
      .finally(() => setLoading(false))
  }, [t])

  const onSave = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setOkMsg(null)
    try {
      const s = await api.put<FirmSmtpStatus>('/firm/smtp', {
        smtpHost: host.trim() || null,
        smtpPort: Number(port) || 465,
        smtpSecure: secure,
        smtpUser: user.trim() || null,
        // undefined = conservar la contraseña actual.
        ...(password ? { smtpPassword: password } : {}),
        smtpFromEmail: fromEmail.trim() || null,
        smtpFromName: fromName.trim() || null,
      })
      setHasPassword(s.hasPassword)
      setPassword('')
      setOkMsg(t('email.savedOk'))
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('email.errors.save')))
    } finally {
      setSaving(false)
    }
  }

  const onTest = async () => {
    setTesting(true)
    setError(null)
    setOkMsg(null)
    try {
      const r = await api.post<{ ok: boolean; to: string }>('/firm/smtp/test', {
        ...(testTo.trim() ? { to: testTo.trim() } : {}),
      })
      setOkMsg(t('email.testSentOk', { to: r.to }))
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('email.errors.test')))
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> {t('email.loading')}
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">{t('email.title')}</h2>
        <p className="text-sm t-muted mt-1">
          {t('email.subtitle')}
        </p>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {okMsg && (
        <div className="surface p-3 text-sm rounded-lg border text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40 flex items-center gap-2">
          <CheckCircle2 size={14} /> {okMsg}
        </div>
      )}

      <form onSubmit={onSave} className="surface p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs t-muted mb-1">{t('email.fields.host')}</label>
          <input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder={t('email.placeholders.host')}
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1">{t('email.fields.port')}</label>
            <input
              type="number"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              className={inputCls}
            />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={secure}
              onChange={(e) => setSecure(e.target.checked)}
            />
            {t('email.fields.tls')}
          </label>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('email.fields.user')}</label>
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder={t('email.placeholders.user')}
            autoComplete="off"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">
            {t('email.fields.password')} {hasPassword && <span className="t-faint">{t('email.passwordSavedHint')}</span>}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={hasPassword ? '••••••••' : t('email.placeholders.password')}
            autoComplete="new-password"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('email.fields.fromEmail')}</label>
          <input
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder={t('email.placeholders.fromEmail')}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('email.fields.fromName')}</label>
          <input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder={t('email.placeholders.fromName')}
            className={inputCls}
          />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t('email.save')}
          </button>
        </div>
      </form>

      <div className="surface p-4 space-y-3">
        <h3 className="t-primary font-semibold text-sm">{t('email.test.title')}</h3>
        <p className="text-xs t-muted">
          {t('email.test.description')}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder={t('email.test.toPlaceholder')}
            className={inputCls}
          />
          <button
            type="button"
            disabled={testing}
            onClick={() => void onTest()}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5 whitespace-nowrap"
          >
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {t('email.test.send')}
          </button>
        </div>
      </div>
    </div>
  )
}
